/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import verificationService from '../../services/verificationService';
import { useVendorVerification } from '../../hooks/useVendorVerification';
import {
  FileText,
  Building,
  MapPin,
  CheckCircle,
  Loader2,
  ArrowLeft,
  ShieldCheck,
  Clock,
  Receipt,
} from 'lucide-react';
import Button from '../../components/shared/Button';
import { useToastContext } from '../../contexts/ToastContext';
import { useServiceCategories } from '../../services/serviceCategoryService';
import useOperatingMarkets from '../../hooks/useOperatingMarkets';
import { countryRequiresPostalCode } from '../../utils/countryPostal';
import { isConfiguredMarket } from '../../utils/marketOptions';
import useGeolocation from '../../hooks/useGeolocation';
import Step1BusinessInfo from '../../components/vendor/verification/Step1BusinessInfo';
import Step2ContactLocation from '../../components/vendor/verification/Step2ContactLocation';
import Step3TaxProfile from '../../components/vendor/verification/Step3TaxProfile';
import Step3Documents from '../../components/vendor/verification/Step3Documents';
import Step4Review from '../../components/vendor/verification/Step4Review';
import AddPlatformModal from '../../components/vendor/verification/AddPlatformModal';

const VendorVerification = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isVendorVerified, isVerificationPending, isLoading, refreshVerificationStatus } =
    useVendorVerification();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { success, error: showError, info } = useToastContext();
  const hasNavigatedRef = useRef(false);
  const justSubmittedRef = useRef(false);
  const {
    country: geoCountry,
    countryCode: geoCountryCode,
    city: geoCity,
    state: geoState,
    loading: geoLoading,
    getCurrentLocation,
  } = useGeolocation();

  useEffect(() => {
    if (!isLoading && isVendorVerified && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      success('You have already completed verification!', 'success');
      navigate('/vendor');
    }
  }, [isLoading, isVendorVerified, navigate, success]);

  useEffect(() => {
    if (!isLoading && isVerificationPending && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;

      if (!justSubmittedRef.current) {
        info(
          'Verification Pending',
          'Your verification is currently pending review. Please wait for admin approval.'
        );
      }

      navigate('/vendor');
    }
  }, [isLoading, isVerificationPending, navigate, info]);

  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useServiceCategories({ active: true });

  const serviceCategories = categoriesData?.data?.data || [];

  const {
    markets,
    options: marketOptions,
    isLoading: marketsLoading,
    error: marketsError,
  } = useOperatingMarkets({ activeOnly: true });

  const [formData, setFormData] = useState({
    business_name: '',
    service_categories: null,
    business_description: '',
    years_experience: '',
    specialties: [],
    business_phone: '',
    business_email: '',
    website: '',
    social_media: {},
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    business_license: null,
    insurance_certificate: null,
    id_document: null,
    insurance_info: '',
    business_hours: {
      monday: { available: true },
      tuesday: { available: true },
      wednesday: { available: true },
      thursday: { available: true },
      friday: { available: true },
      saturday: { available: true },
      sunday: { available: true },
    },
    team_size: '',
    certifications: '',
    references: '',
    // Tax profile (verification step 3)
    legal_name: '',
    tax_business_name: '',
    business_type: '',
    tax_residence_country: '',
    tax_address_line1: '',
    tax_address_line2: '',
    tax_city: '',
    tax_region_state: '',
    tax_postal_code: '',
    tax_id: '',
    gst_hst_registered: false,
    gst_hst_number: '',
    vat_registered: false,
    vat_number: '',
    us_sales_tax_registered: false,
    w9_status: '',
    payout_country: '',
    payout_currency: 'USD',
  });

  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [showAddPlatformModal, setShowAddPlatformModal] = useState(false);
  const [newPlatformName, setNewPlatformName] = useState('');

  const steps = [
    { id: 1, title: 'Business', description: 'Who you are', icon: Building },
    { id: 2, title: 'Contact', description: 'How clients reach you', icon: MapPin },
    { id: 3, title: 'Tax', description: 'Legal & tax details', icon: Receipt },
    { id: 4, title: 'Documents', description: 'Proof & details', icon: FileText },
    { id: 5, title: 'Review', description: 'Confirm & submit', icon: CheckCircle },
  ];

  const progressPct = Math.round(((currentStep - 1) / (steps.length - 1)) * 100);

  useEffect(() => {
    if (user) {
      setFormData((prev) => {
        const updated = { ...prev };

        if (user.vendor_profile) {
          Object.assign(updated, user.vendor_profile);
          updated.specialties = user.vendor_profile.specialties || [];
        }

        if (!updated.business_email && user.email) {
          updated.business_email = user.email;
        }
        if (!updated.business_phone && user.phone) {
          updated.business_phone = user.phone;
        }
        if (!updated.city && user.city) {
          updated.city = user.city;
        }
        if (!updated.state && user.state) {
          updated.state = user.state;
        }
        if (!updated.address && user.address) {
          updated.address = user.address;
        }
        if (!updated.zip_code && user.zip_code) {
          updated.zip_code = user.zip_code;
        }

        return updated;
      });
    }
  }, [user]);

  // Do not auto-stamp IP/geo country onto the form — vendor must choose operating country.
  // Geo can be applied explicitly via "Suggest from detected location" on step 2.

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field] || (field === 'country' && errors.zip_code)) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
        ...(field === 'country' && !countryRequiresPostalCode(value) ? { zip_code: null } : {}),
      }));
    }
  };

  const handleSpecialtyToggle = (specialty) => {
    setFormData((prev) => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter((s) => s !== specialty)
        : [...prev.specialties, specialty],
    }));
  };

  const handleSocialMediaChange = (platform, value) => {
    setFormData((prev) => ({
      ...prev,
      social_media: {
        ...prev.social_media,
        [platform]: value,
      },
    }));
  };

  const addSocialMediaPlatform = () => {
    setNewPlatformName('');
    setShowAddPlatformModal(true);
  };

  const handleAddPlatform = () => {
    if (newPlatformName && newPlatformName.trim()) {
      const normalizedPlatform = newPlatformName.toLowerCase().trim();
      if (!formData.social_media[normalizedPlatform]) {
        handleSocialMediaChange(normalizedPlatform, '');
        setShowAddPlatformModal(false);
        setNewPlatformName('');
      } else {
        showError('This platform already exists!');
      }
    }
  };

  const handleCancelAddPlatform = () => {
    setShowAddPlatformModal(false);
    setNewPlatformName('');
  };

  const removeSocialMediaPlatform = (platform) => {
    setFormData((prev) => {
      const newSocialMedia = { ...prev.social_media };
      delete newSocialMedia[platform];
      return {
        ...prev,
        social_media: newSocialMedia,
      };
    });
  };

  const toggleDayAvailability = (day) => {
    setFormData((prev) => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: {
          ...prev.business_hours[day],
          available: !prev.business_hours[day].available,
        },
      },
    }));
  };

  const handleFileUpload = async (field, file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        [field]: 'Please upload a valid image (JPEG, PNG) or PDF file',
      }));
      return;
    }

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        [field]: 'File size must be less than 10MB',
      }));
      return;
    }

    setUploadProgress((prev) => ({ ...prev, [field]: 0 }));

    try {
      const uploadResult = await verificationService.uploadDocument(file, field, (progress) => {
        setUploadProgress((prev) => ({ ...prev, [field]: progress }));
      });

      setFormData((prev) => ({
        ...prev,
        [field]: {
          file: file,
          name: file.name,
          url: uploadResult.data.url,
          filename: uploadResult.data.filename,
          size: uploadResult.data.size,
          mime_type: uploadResult.data.mime_type,
        },
      }));

      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));

      setUploadProgress((prev) => ({ ...prev, [field]: null }));
    } catch (error) {
      console.error('File upload error:', error);
      setErrors((prev) => ({
        ...prev,
        [field]: error.response?.data?.message || 'Failed to upload file. Please try again.',
      }));
      setUploadProgress((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.business_name.trim()) newErrors.business_name = 'Business name is required';
        if (!formData.service_categories)
          newErrors.service_categories = 'Please select a service category';
        if (!formData.business_description.trim())
          newErrors.business_description = 'Business description is required';
        if (formData.business_description.length > 500)
          newErrors.business_description = 'Business description must not exceed 500 characters';
        if (!formData.years_experience)
          newErrors.years_experience = 'Years of experience is required';
        if (formData.specialties.length === 0)
          newErrors.specialties = 'Please select at least one specialty';
        break;
      case 2:
        if (!formData.business_phone.trim()) newErrors.business_phone = 'Business phone is required';
        if (!formData.business_email.trim()) newErrors.business_email = 'Business email is required';
        if (!formData.address.trim()) newErrors.address = 'Business address is required';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.state.trim()) newErrors.state = 'State is required';
        if (countryRequiresPostalCode(formData.country) && !formData.zip_code.trim()) {
          newErrors.zip_code = 'ZIP code is required';
        }
        if (marketsLoading) {
          newErrors.country = 'Markets are still loading';
        } else if (!isConfiguredMarket(markets, formData.country)) {
          newErrors.country = markets.length
            ? 'Select an operating market'
            : 'No operating markets are set up yet';
        }
        break;
      case 3: {
        const taxCountry = (
          formData.tax_residence_country ||
          formData.country ||
          ''
        ).toUpperCase();
        const market = (markets || []).find(
          (m) => String(m.country_code || '').toUpperCase() === taxCountry
        );
        const required = market?.required_vendor_fields || [
          'legal_name',
          'tax_residence_country',
        ];

        const valueFor = (field) => {
          switch (field) {
            case 'business_name':
              return formData.tax_business_name || formData.business_name;
            case 'address_line1':
              return formData.tax_address_line1 || formData.address;
            case 'city':
              return formData.tax_city || formData.city;
            case 'region_state':
              return formData.tax_region_state || formData.state;
            case 'postal_code':
              return formData.tax_postal_code || formData.zip_code;
            case 'tax_residence_country':
              return formData.tax_residence_country || formData.country;
            case 'gst_hst_registered':
              return formData.gst_hst_registered;
            case 'vat_registered':
              return formData.vat_registered;
            case 'us_sales_tax_registered':
              return formData.us_sales_tax_registered;
            default:
              return formData[field];
          }
        };

        const errorKeyFor = (field) => {
          if (field === 'business_name') return 'tax_business_name';
          if (field === 'address_line1') return 'tax_address_line1';
          if (field === 'city') return 'tax_city';
          if (field === 'region_state') return 'tax_region_state';
          if (field === 'postal_code') return 'tax_postal_code';
          return field;
        };

        required.forEach((field) => {
          if (field === 'postal_code' && !countryRequiresPostalCode(taxCountry)) {
            return;
          }
          const val = valueFor(field);
          const key = errorKeyFor(field);
          if (typeof val === 'boolean') {
            // registered flags can be false — still "answered"
            return;
          }
          if (val === null || val === undefined || String(val).trim() === '') {
            newErrors[key] = 'Required for your market';
          }
        });

        if (!formData.legal_name?.trim() && !newErrors.legal_name) {
          newErrors.legal_name = 'Legal name is required';
        }
        if (!(formData.tax_residence_country || formData.country) && !newErrors.tax_residence_country) {
          newErrors.tax_residence_country = 'Tax residence market is required';
        } else if (
          !marketsLoading &&
          !isConfiguredMarket(markets, formData.tax_residence_country || formData.country)
        ) {
          newErrors.tax_residence_country = 'Select a market configured in admin';
        }
        break;
      }
      case 4:
        if (!formData.business_license?.url) {
          newErrors.business_license = 'Business license is required';
        }
        if (!formData.insurance_certificate?.url) {
          newErrors.insurance_certificate = 'Insurance certificate is required';
        }
        if (!formData.id_document?.url) {
          newErrors.id_document = 'ID document is required';
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setFormData((prev) => {
        if (currentStep !== 2) return prev;
        // Seed tax fields from contact/location when entering Tax step
        return {
          ...prev,
          tax_residence_country: prev.tax_residence_country || prev.country || '',
          tax_business_name: prev.tax_business_name || prev.business_name || '',
          tax_address_line1: prev.tax_address_line1 || prev.address || '',
          tax_city: prev.tax_city || prev.city || '',
          tax_region_state: prev.tax_region_state || prev.state || '',
          tax_postal_code: prev.tax_postal_code || prev.zip_code || '',
          payout_country: prev.payout_country || prev.country || '',
          legal_name: prev.legal_name || prev.business_name || '',
        };
      });
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      setCurrentStep(4);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!validateStep(3)) {
      setCurrentStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    justSubmittedRef.current = true;
    hasNavigatedRef.current = false;

    try {
      const verificationData = {
        user_id: user.id,
        verification_type: 'vendor_business',
        verification_status: 'pending',
        verification_data: {
          business_name: formData.business_name?.trim(),
          service_categories: formData.service_categories ? [formData.service_categories] : [],
          business_description: formData.business_description?.trim(),
          years_experience: formData.years_experience,
          specialties: formData.specialties || [],
          business_phone: formData.business_phone?.trim(),
          business_email: formData.business_email?.trim(),
          website: formData.website?.trim(),
          social_media: formData.social_media,
          address: formData.address?.trim(),
          city: formData.city?.trim(),
          state: formData.state?.trim(),
          zip_code: formData.zip_code?.trim(),
          country: formData.country,
          insurance_info: formData.insurance_info?.trim(),
          business_hours: formData.business_hours,
          team_size: formData.team_size?.trim(),
          certifications: formData.certifications?.trim(),
          references: formData.references?.trim(),
        },
        verification_document_url: {
          business_license: formData.business_license?.url || null,
          insurance_certificate: formData.insurance_certificate?.url || null,
          id_document: formData.id_document?.url || null,
        },
      };

      const vendorProfileData = {
        business_name: formData.business_name?.trim(),
        description: formData.business_description?.trim(),
        address: formData.address?.trim(),
        city: formData.city?.trim(),
        state: formData.state?.trim(),
        zip_code: formData.zip_code?.trim(),
        country: formData.country,
        website: formData.website?.trim(),
        business_phone: formData.business_phone?.trim() || null,
        social_media: formData.social_media,
        years_experience: parseInt(formData.years_experience) || 0,
        specialties: formData.specialties || [],
        insurance_info: formData.insurance_info?.trim(),
        business_hours: formData.business_hours,
        team_size: parseInt(formData.team_size) || null,
        certifications: formData.certifications?.trim() || null,
        references: formData.references?.trim() || null,
        service_categories: formData.service_categories ? [formData.service_categories] : [],
        is_verified: true,
      };

      const taxCountry = (
        formData.tax_residence_country ||
        formData.country ||
        ''
      ).toUpperCase();

      const taxProfileData = {
        legal_name: formData.legal_name?.trim() || null,
        business_name:
          formData.tax_business_name?.trim() || formData.business_name?.trim() || null,
        business_type: formData.business_type || null,
        tax_residence_country: taxCountry || null,
        region_state: formData.tax_region_state?.trim() || formData.state?.trim() || null,
        city: formData.tax_city?.trim() || formData.city?.trim() || null,
        address_line1: formData.tax_address_line1?.trim() || formData.address?.trim() || null,
        address_line2: formData.tax_address_line2?.trim() || null,
        postal_code: formData.tax_postal_code?.trim() || formData.zip_code?.trim() || null,
        tax_id: formData.tax_id?.trim() || null,
        gst_hst_registered: Boolean(formData.gst_hst_registered),
        gst_hst_number: formData.gst_hst_number?.trim() || null,
        vat_registered: Boolean(formData.vat_registered),
        vat_number: formData.vat_number?.trim() || null,
        us_sales_tax_registered: Boolean(formData.us_sales_tax_registered),
        w9_status: formData.w9_status || null,
        payout_country: (
          formData.payout_country ||
          formData.tax_residence_country ||
          formData.country ||
          ''
        ).toUpperCase() || null,
        payout_currency: (formData.payout_currency || 'USD').toUpperCase(),
        mark_complete: true,
      };

      await verificationService.submitVerificationWithProfile({
        verification_data: verificationData.verification_data,
        verification_document_url: verificationData.verification_document_url,
        vendor_profile_data: vendorProfileData,
        tax_profile: taxProfileData,
      });

      success('Verification completed successfully! Your vendor account has been verified.');

      await refreshVerificationStatus();

      setTimeout(() => {
        if (!hasNavigatedRef.current) {
          hasNavigatedRef.current = true;
          navigate('/vendor');
        }
        justSubmittedRef.current = false;
      }, 300);
    } catch (error) {
      console.error('Verification submission error:', error);
      const errorMessage =
        error.response?.data?.message || 'Failed to submit verification. Please try again.';
      showError(errorMessage);
      justSubmittedRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const StatusCard = ({ icon: Icon, iconClass, title, body, footer }) => (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl shadow-gray-900/5 p-8 text-center">
        <div
          className={`mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border ${iconClass}`}
        >
          <Icon className="h-7 w-7" />
        </div>
        <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-50 mb-2">
          {title}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">{body}</p>
        {footer ? (
          <p className="text-xs text-gray-500 dark:text-gray-500">{footer}</p>
        ) : null}
      </div>
    </div>
  );

  const VerificationSkeleton = () => (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 animate-pulse space-y-6">
        <div className="h-9 w-40 rounded-xl bg-gray-200 dark:bg-gray-800" />
        <div className="space-y-3">
          <div className="h-8 w-56 rounded-lg bg-gray-200 dark:bg-gray-800" />
          <div className="h-4 w-80 max-w-full rounded bg-gray-200 dark:bg-gray-800" />
        </div>
        <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-800" />
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 space-y-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-28 rounded bg-gray-200 dark:bg-gray-800" />
              <div className="h-11 w-full rounded-xl bg-gray-200 dark:bg-gray-800" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    if (currentStep === 1 && categoriesLoading) {
      return (
        <div className="space-y-5 animate-pulse">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-28 rounded bg-gray-200 dark:bg-gray-800" />
              <div className="h-11 w-full rounded-xl bg-gray-200 dark:bg-gray-800" />
            </div>
          ))}
        </div>
      );
    }

    if (currentStep === 2 && marketsLoading) {
      return (
        <div className="space-y-5 animate-pulse">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-28 rounded bg-gray-200 dark:bg-gray-800" />
              <div className="h-11 w-full rounded-xl bg-gray-200 dark:bg-gray-800" />
            </div>
          ))}
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <Step1BusinessInfo
            formData={formData}
            errors={errors}
            serviceCategories={serviceCategories}
            categoriesLoading={categoriesLoading}
            categoriesError={categoriesError}
            handleInputChange={handleInputChange}
            handleSpecialtyToggle={handleSpecialtyToggle}
          />
        );
      case 2:
        return (
          <Step2ContactLocation
            formData={formData}
            errors={errors}
            markets={markets}
            marketOptions={marketOptions}
            marketsLoading={marketsLoading}
            marketsError={marketsError}
            geoHint={{
              country: geoCountry,
              countryCode: geoCountryCode,
              city: geoCity,
              state: geoState,
              loading: geoLoading,
            }}
            onSuggestFromGeo={async () => {
              const detected = await getCurrentLocation?.({ preferIp: true });
              if (!detected) {
                showError('Could not detect a location hint. Select your country manually.');
                return;
              }
              const code = (detected.countryCode || '').toUpperCase();
              const detectedIsMarket = isConfiguredMarket(markets, code);
              setFormData((prev) => ({
                ...prev,
                country: detectedIsMarket ? code : prev.country,
                city: detected.city || prev.city,
                state: detected.state || prev.state,
              }));
              info(
                detectedIsMarket ? 'Suggested from current connection' : 'Detected country is not a market',
                detectedIsMarket
                  ? 'Review the market before continuing. This does not change your storefront until you submit.'
                  : 'City was filled in. Choose an operating market from the list.'
              );
            }}
            handleInputChange={handleInputChange}
            handleSocialMediaChange={handleSocialMediaChange}
            removeSocialMediaPlatform={removeSocialMediaPlatform}
            addSocialMediaPlatform={addSocialMediaPlatform}
            showAddPlatformModal={showAddPlatformModal}
            setShowAddPlatformModal={setShowAddPlatformModal}
            newPlatformName={newPlatformName}
            setNewPlatformName={setNewPlatformName}
            handleAddPlatform={handleAddPlatform}
            handleCancelAddPlatform={handleCancelAddPlatform}
          />
        );
      case 3:
        return (
          <Step3TaxProfile
            formData={formData}
            errors={errors}
            markets={markets}
            marketOptions={marketOptions}
            handleInputChange={handleInputChange}
          />
        );
      case 4:
        return (
          <Step3Documents
            formData={formData}
            errors={errors}
            uploadProgress={uploadProgress}
            handleFileUpload={handleFileUpload}
            handleInputChange={handleInputChange}
            toggleDayAvailability={toggleDayAvailability}
          />
        );
      case 5:
        return (
          <Step4Review
            formData={formData}
            serviceCategories={serviceCategories}
            marketOptions={marketOptions}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return <VerificationSkeleton />;
  }

  if (isVendorVerified) {
    return (
      <StatusCard
        icon={ShieldCheck}
        iconClass="border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400"
        title="Already verified"
        body="You have already completed the verification process."
        footer="Redirecting to dashboard…"
      />
    );
  }

  if (isVerificationPending) {
    return (
      <StatusCard
        icon={Clock}
        iconClass="border-amber-200 bg-amber-50 text-amber-600 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400"
        title="Verification pending"
        body="Your verification is under review. You’ll get an email once it’s complete."
        footer="Redirecting to dashboard…"
      />
    );
  }

  const activeStep = steps.find((s) => s.id === currentStep) || steps[0];

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-50 dark:bg-gray-950">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(ellipse_at_top,_rgba(15,23,42,0.05),_transparent_65%)] dark:bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.04),_transparent_65%)]"
        aria-hidden
      />

      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-32 sm:pb-36">
        <button
          type="button"
          onClick={() => navigate('/vendor')}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Dashboard
        </button>

        <header className="mt-6 mb-6 sm:mb-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400 mb-2">
            Identity · Step {currentStep} of {steps.length}
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
            Verify your business
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed max-w-lg">
            {activeStep.description}. Clients trust verified vendors — business, tax, and documents in one pass.
          </p>
        </header>

        {/* Compact stepper — always one row */}
        <nav aria-label="Verification steps" className="mb-6 sm:mb-8">
          <ol className="flex items-start justify-between gap-0.5 sm:gap-1">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              const isLast = index === steps.length - 1;

              return (
                <li key={step.id} className="flex-1 min-w-0 flex items-start">
                  <div className="flex flex-col items-center w-full min-w-0">
                    <div className="flex items-center w-full">
                      {index > 0 ? (
                        <div
                          className={`h-0.5 flex-1 rounded-full transition-colors ${
                            isCompleted || isActive
                              ? 'bg-gray-900 dark:bg-gray-100'
                              : 'bg-gray-200 dark:bg-gray-800'
                          }`}
                          aria-hidden
                        />
                      ) : (
                        <div className="flex-1" aria-hidden />
                      )}
                      <div
                        className={`
                          relative z-[1] flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full
                          border-2 transition-all duration-300
                          ${
                            isCompleted
                              ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm shadow-emerald-600/20'
                              : isActive
                                ? 'border-gray-900 bg-gray-900 text-white shadow-md shadow-gray-900/20 dark:border-gray-100 dark:bg-gray-100 dark:text-gray-900'
                                : 'border-gray-200 bg-white text-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-500'
                          }
                        `}
                        aria-current={isActive ? 'step' : undefined}
                      >
                        {isCompleted ? (
                          <CheckCircle className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
                        ) : (
                          <StepIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        )}
                      </div>
                      {!isLast ? (
                        <div
                          className={`h-0.5 flex-1 rounded-full transition-colors ${
                            isCompleted
                              ? 'bg-gray-900 dark:bg-gray-100'
                              : 'bg-gray-200 dark:bg-gray-800'
                          }`}
                          aria-hidden
                        />
                      ) : (
                        <div className="flex-1" aria-hidden />
                      )}
                    </div>
                    <p
                      className={`mt-2 text-[10px] sm:text-[11px] font-semibold tracking-wide truncate max-w-full px-0.5 text-center ${
                        isActive
                          ? 'text-gray-900 dark:text-gray-50'
                          : isCompleted
                            ? 'text-emerald-700 dark:text-emerald-400'
                            : 'text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {step.title}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="mt-4 h-1 rounded-full bg-gray-200/80 dark:bg-gray-800 overflow-hidden">
            <div
              className="h-full rounded-full bg-gray-900 dark:bg-gray-100 transition-all duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </nav>

        {/* Form */}
        <div className="rounded-2xl border border-gray-200/90 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 sm:p-7">
          {renderCurrentStep()}
        </div>

        {/* Actions */}
        <div className="fixed bottom-0 inset-x-0 z-40 border-t border-gray-200/80 dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur-md pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-3.5 pb-1 flex items-center justify-between gap-3">
            <Button
              onClick={handlePrevious}
              variant="outline"
              disabled={currentStep === 1 || isSubmitting}
              className="!rounded-xl min-w-[6.5rem]"
            >
              Previous
            </Button>

            <span className="hidden sm:inline text-xs text-gray-400 tabular-nums">
              {progressPct}% complete
            </span>

            {currentStep < 5 ? (
              <Button onClick={handleNext} className="!rounded-xl min-w-[7.5rem]">
                Continue
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="!rounded-xl inline-flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    Submit
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      <AddPlatformModal
        isOpen={showAddPlatformModal}
        newPlatformName={newPlatformName}
        setNewPlatformName={setNewPlatformName}
        onAdd={handleAddPlatform}
        onCancel={handleCancelAddPlatform}
      />
    </div>
  );
};

export default VendorVerification;
